import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { JBMSelectOption } from '../../../../components/JBM/Forms/JBMFormGroups/JBMSelect/JBMSelect.component';
import { CRUDAction } from '../../../../components/JBM/Views/JBMCRUDTitle/JBMCRUDTitle.component';
import { JBMToastsService } from '../../../../components/JBM/Views/JBMToasts/JBMToasts.service';
import { DatetimeService } from '../../../../services/datetime/datetime.service';
import { PriceService } from '../../../../services/price/price.service';
import { TranslateService } from '../../../../services/multilingual/translate.service';
import { PriceAgreement, SubjectType } from '../../data/interfaces/price-agreement';
import {
  AdjustmentDirection,
  PriceAgreementRule,
  PriceCalculationType,
  ProductType
} from '../../data/interfaces/price-agreement-rule';
import { PriceAgreementsService } from '../../data/price-agreements.service';

@Component({
  selector: 'price-agreement',
  templateUrl: './price-agreement.component.html',
  styleUrls: ['./price-agreement.component.scss']
})
export class PriceAgreementComponent implements OnInit {
  form: FormGroup;
  priceAgreement: PriceAgreement;
  isSubmitted: boolean = false;
  loading: boolean = false;
  warning: string = '';
  action: CRUDAction = CRUDAction.Create;

  calculationTypeOptions: JBMSelectOption[] = [];
  adjustmentDirectionOptions: JBMSelectOption[] = [];

  @Input() customer_id: number = 0;
  @Input() project_id: number = 0;
  @Input() product_type: ProductType;
  @Input() product_id: number = 0;
  @Input() price_before: number = 0;
  @Input() agreement: PriceAgreement;

  @Output() onSave = new EventEmitter<PriceAgreement>();
  @Output() onCancel = new EventEmitter<void>();

  constructor(
    private formBuilder: FormBuilder,
    private DatetimeService: DatetimeService,
    private PriceService: PriceService,
    private PriceAgreementsService: PriceAgreementsService,
    private TranslateService: TranslateService,
    private JBMToastsService: JBMToastsService
  ) { }

  ngOnInit(): void {
    this.setOptions();
    this.addControls();
    this.setPriceAgreement(this.agreement || this.PriceAgreementsService.getEmpty());
  }

  addControls(): void {
    this.form = this.formBuilder.group({
      description: new FormControl('', [Validators.required]),
      valid_from: new FormControl('', [
        Validators.required,
        this.DatetimeService.dateValidator(this.DatetimeService, true)
      ]),
      valid_to: new FormControl('', [
        this.DatetimeService.dateValidator(this.DatetimeService, false)
      ]),
      blocked: new FormControl(false),
      price_calculation_type: new FormControl('', [Validators.required]),
      adjustment_direction: new FormControl(null),
      value: new FormControl('', [
        this.PriceService.priceValidator(this.PriceService, false, true)
      ])
    }, { updateOn: 'blur' });
  }

  get formControls() {
    return this.form.controls;
  }

  get requiresAdjustmentDirection(): boolean {
    const calculationType = Number(this.formControls.price_calculation_type.value);
    return calculationType === PriceCalculationType.PercentageAdjustment ||
      calculationType === PriceCalculationType.AmountAdjustment;
  }

  get priceBeforeDisplay(): string {
    const price = this.PriceService.formatPrice(this.price_before);
    return price ? '€ ' + price : '€ 0,00';
  }

  get valuePrepend(): string {
    return Number(this.formControls.price_calculation_type.value) === PriceCalculationType.PercentageAdjustment
      ? ''
      : '€';
  }

  get valueAppend(): string {
    return Number(this.formControls.price_calculation_type.value) === PriceCalculationType.PercentageAdjustment
      ? '%'
      : '';
  }

  get subjectLabel(): string {
    return this.project_id > 0 ? 'Project' : 'Customer';
  }

  get subjectId(): number {
    return this.project_id > 0 ? this.project_id : this.customer_id;
  }

  get productTypeLabel(): string {
    switch(Number(this.product_type)) {
      case ProductType.Concrete:
        return 'Concrete';
      case ProductType.Article:
        return 'Article';
      case ProductType.Resource:
        return 'Resource';
      default:
        return '';
    }
  }

  setOptions(): void {
    this.calculationTypeOptions = [
      { key: PriceCalculationType.FixedPrice.toString(), value: 'Fixed price' },
      { key: PriceCalculationType.PercentageAdjustment.toString(), value: 'Percentage adjustment' },
      { key: PriceCalculationType.AmountAdjustment.toString(), value: 'Amount adjustment' }
    ];
    this.adjustmentDirectionOptions = [
      { key: AdjustmentDirection.Discount.toString(), value: 'Discount' },
      { key: AdjustmentDirection.Surcharge.toString(), value: 'Surcharge' }
    ];
  }

  setPriceAgreement(priceAgreement: PriceAgreement): void {
    this.priceAgreement = priceAgreement;
    this.action = priceAgreement.id > 0 ? CRUDAction.Update : CRUDAction.Create;

    if(priceAgreement.subject_id > 0) {
      if(priceAgreement.subject_type === SubjectType.Project)
        this.project_id = priceAgreement.subject_id;
      else
        this.customer_id = priceAgreement.subject_id;
    }

    const rule = priceAgreement.rules && priceAgreement.rules.length
      ? priceAgreement.rules[0]
      : this.getEmptyRule();

    if(rule.product_id > 0) {
      this.product_type = rule.product_type;
      this.product_id = rule.product_id;
    }

    let validFrom = priceAgreement.valid_from;
    let validTo = priceAgreement.valid_to || '';
    if(!validFrom)
      validFrom = this.DatetimeService.getDateDMYFromParts(
        this.DatetimeService.getCurrentDay(),
        this.DatetimeService.getCurrentMonth(),
        this.DatetimeService.getCurrentYear()
      );
    if(!validTo)
      validTo = this.DatetimeService.getDateDMYFromParts(
        31, 12, this.DatetimeService.getCurrentYear()
      );

    this.formControls.description.setValue(priceAgreement.description || '');
    this.formControls.valid_from.setValue(validFrom);
    this.formControls.valid_to.setValue(validTo);
    this.formControls.blocked.setValue(priceAgreement.blocked || false);
    this.formControls.price_calculation_type.setValue(rule.price_calculation_type.toString());
    this.formControls.adjustment_direction.setValue(
      rule.adjustment_direction === null ? null : rule.adjustment_direction.toString()
    );
    this.formControls.value.setValue(
      rule.value ? this.PriceService.formatPrice(rule.value) : this.PriceService.formatPrice(this.price_before)
    );
    this.setAdjustmentDirectionValidators();
  }

  getEmptyRule(): PriceAgreementRule {
    return {
      id: 0,
      price_agreements_id: 0,
      product_type: this.product_type,
      product_id: this.product_id,
      price_calculation_type: PriceCalculationType.FixedPrice,
      adjustment_direction: null,
      value: this.price_before || 0
    };
  }

  calculationTypeChange(): void {
    this.setAdjustmentDirectionValidators();
  }

  setAdjustmentDirectionValidators(): void {
    const direction = this.formControls.adjustment_direction;
    if(this.requiresAdjustmentDirection)
      direction.setValidators([Validators.required]);
    else {
      direction.clearValidators();
      direction.setValue(null);
    }
    direction.updateValueAndValidity();
  }

  save(): void {
    this.isSubmitted = true;
    this.warning = '';
    this.setAdjustmentDirectionValidators();

    if(this.subjectId <= 0) {
      this.warning = 'A customer or project is required.';
      return;
    }
    if(this.product_type === undefined || this.product_type === null || this.product_id <= 0) {
      this.warning = 'A product type and product are required.';
      return;
    }

    const validFrom = this.DatetimeService.dateDMYToYMD(this.formControls.valid_from.value);
    const validTo = this.formControls.valid_to.value
      ? this.DatetimeService.dateDMYToYMD(this.formControls.valid_to.value)
      : null;
    if(validTo && validFrom > validTo) {
      this.warning = this.TranslateService.GetTranslation('entity.to-date-before-from-date');
      return;
    }
    if(this.form.invalid)
      return;

    const existingRule = this.priceAgreement.rules && this.priceAgreement.rules.length
      ? this.priceAgreement.rules[0]
      : this.getEmptyRule();
    const rule: PriceAgreementRule = {
      id: existingRule.id || 0,
      price_agreements_id: this.priceAgreement.id,
      product_type: Number(this.product_type) as ProductType,
      product_id: Number(this.product_id),
      price_calculation_type: Number(this.formControls.price_calculation_type.value) as PriceCalculationType,
      adjustment_direction: this.requiresAdjustmentDirection
        ? Number(this.formControls.adjustment_direction.value) as AdjustmentDirection
        : null,
      value: this.PriceService.getPriceFromStr(this.formControls.value.value)
    };
    const data: PriceAgreement = {
      id: this.priceAgreement.id,
      subject_type: this.project_id > 0 ? SubjectType.Project : SubjectType.Customer,
      subject_id: this.subjectId,
      description: this.formControls.description.value,
      valid_from: validFrom,
      valid_to: validTo,
      blocked: this.formControls.blocked.value,
      rules: [rule]
    };

    this.loading = true;
    const request = data.id > 0
      ? this.PriceAgreementsService.update(data)
      : this.PriceAgreementsService.create(data);
    request.subscribe(
      () => {},
      (error: any) => this.handleError(error),
      () => this.afterSave(data)
    );
  }

  afterSave(data: PriceAgreement): void {
    this.loading = false;
    this.JBMToastsService.success(this.TranslateService.GetTranslation('ui.entity-saved'));
    this.onSave.emit(data);
  }

  handleError(error: any): void {
    this.loading = false;
    const message = error && error.error && typeof error.error === 'string'
      ? error.error
      : 'The price agreement could not be saved.';
    this.JBMToastsService.error(message);
    console.error(error);
  }

  cancel(): void {
    this.onCancel.emit();
  }
}
